/**
 * 🚀 Modern Firebase Alert Manager
 * Scalable, modular, and production-ready alert system
 * Built with latest JavaScript features and Firebase v10+
 * 
 * Features:
 * - 🔥 Real-time Firebase integration
 * - 📧 Email notifications via Firebase Functions
 * - 🔔 Push notifications with service workers
 * - 📊 Analytics and reporting
 * - 🛡️ Security and validation
 * - 🔄 Offline support and retry logic
 * - 📱 Progressive Web App ready
 * 
 * @version 2.0.0
 * @author Kent Traders
 * @license MIT
 */

// Use unified Firebase manager instead of direct imports
// This ensures consistency with the loyalty system

/**
 * 🔧 Configuration Manager
 * Handles environment-specific configurations
 */
// Prevent duplicate class declaration
if (typeof window.ConfigManager === 'undefined') {
  window.ConfigManager = class ConfigManager {
      constructor() {
      this.config = {
        // Import unified Firebase configuration
        // <script src="/config/unified-firebase-config.js"></script>

        // Unified Firebase config for both loyalty and alert systems
        firebase: window.UNIFIED_FIREBASE_CONFIG || {
          apiKey: "",
          authDomain: "window.__ENV__.FIREBASE_AUTH_DOMAIN",
          projectId: "window.__ENV__.FIREBASE_PROJECT_ID",
          storageBucket: "window.__ENV__.FIREBASE_STORAGE_BUCKET",
          messagingSenderId: "window.__ENV__.FIREBASE_MESSAGING_SENDER_ID",
          appId: "window.__ENV__.FIREBASE_APP_ID"
          // measurementId removed to avoid conflicts
        },
      
      // Alert system settings
      alerts: {
        maxSubscriptionsPerEmail: 50,
        maxSubscriptionsPerProduct: 1000,
        notificationCooldown: 24 * 60 * 60 * 1000, // 24 hours
        retryAttempts: 3,
        retryDelay: 5000, // 5 seconds
      },
      
      // Feature flags
      features: {
        pushNotifications: true,
        emailNotifications: true,
        analytics: true,
        offlineSupport: true,
        realtimeUpdates: true
      }
    };
  }

  get(key) {
    return key.split('.').reduce((obj, k) => obj?.[k], this.config);
  }

  set(key, value) {
    const keys = key.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((obj, k) => obj[k] = obj[k] || {}, this.config);
    target[lastKey] = value;
  }
  };
}

// Guard against duplicate execution (e.g., idle loader + service worker)
if (window.__KT_ALERT_MANAGER_LOADED__) {
  // Already initialized; prevent re-registration
  // eslint-disable-next-line no-console
  console.info('🚀 KT Alert Manager: Already loaded, skipping duplicate init');
} else {
  window.__KT_ALERT_MANAGER_LOADED__ = true;
}

/**
 * 🛡️ Validation Manager
 * Handles data validation and sanitization
 */
if (typeof window.ValidationManager === 'undefined') {
  window.ValidationManager = class ValidationManager {
    static validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email) && email.length <= 254;
    }

    static validateProductId(productId) {
      return typeof productId === 'string' && productId.length > 0;
    }

    static validateVariantId(variantId) {
      return typeof variantId === 'string' && variantId.length > 0;
    }

    static validateAlertType(alertType) {
      return ['back_in_stock', 'price_drop'].includes(alertType);
    }

    static validateSubscriptionData(data) {
      const errors = [];
      
      if (!this.validateEmail(data.email)) {
        errors.push('Invalid email address');
      }
      
      if (!this.validateProductId(data.product_id)) {
        errors.push('Invalid product ID');
      }
      
      if (!this.validateVariantId(data.variant_id)) {
        errors.push('Invalid variant ID');
      }
      
      if (!this.validateAlertType(data.alert_type)) {
        errors.push('Invalid alert type');
      }
      
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  };
}
// If ValidationManager already exists on window, it's already available - no action needed

/**
 * 📊 Analytics Manager
 * Handles event tracking and analytics
 */
class AnalyticsManager {
  constructor(analytics) {
    this.analytics = analytics;
    this.events = {
      ALERT_SUBSCRIBED: 'alert_subscribed',
      ALERT_UNSUBSCRIBED: 'alert_unsubscribed',
      NOTIFICATION_SENT: 'notification_sent',
      NOTIFICATION_CLICKED: 'notification_clicked',
      ERROR_OCCURRED: 'error_occurred'
    };
  }

  logEvent(eventName, parameters = {}) {
    try {
      if (this.analytics && this.config.get('features.analytics')) {
        logEvent(this.analytics, eventName, {
          timestamp: Date.now(),
          ...parameters
        });
      }
    } catch (error) {
      console.warn('Analytics error:', error);
    }
  }

  logAlertSubscribed(data) {
    this.logEvent(this.events.ALERT_SUBSCRIBED, {
      product_id: data.product_id,
      variant_id: data.variant_id,
      alert_type: data.alert_type,
      notification_methods: data.notifications
    });
  }

  logError(error, context = {}) {
    this.logEvent(this.events.ERROR_OCCURRED, {
      error_message: error.message,
      error_stack: error.stack,
      ...context
    });
  }
}

/**
 * 🔄 Retry Manager
 * Handles retry logic for failed operations
 */
class RetryManager {
  constructor(config) {
    this.maxAttempts = config.get('alerts.retryAttempts');
    this.delay = config.get('alerts.retryDelay');
  }

  async executeWithRetry(operation, context = {}) {
    let lastError;
    
    for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        console.warn(`Retry attempt ${attempt}/${this.maxAttempts} failed:`, error);
        
        if (attempt < this.maxAttempts) {
          await this.delay(attempt * this.delay);
        }
      }
    }
    
    throw lastError;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * 🔔 Notification Manager
 * Handles push notifications and service worker registration
 */
class NotificationManager {
  constructor() {
    this.supported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
  }

  async initialize() {
    if (!this.supported) {
      return false;
    }

    // Service worker registration disabled — Shopify serves assets from CDN
    // (cdn.shopify.com), so /assets/sw-push.js doesn't exist at the store
    // origin, causing a 404. SW also can't be registered cross-origin.
    // Push notifications will use the existing SW if one is already active.
    try {
      this.registration = await navigator.serviceWorker.ready;
      return true;
    } catch (error) {
      return false;
    }
  }

  async requestPermission() {
    if (!this.supported) return false;

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  async subscribeToPush(vapidPublicKey) {
    if (!this.registration) return null;

    try {
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      return null;
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

/**
 * 🔥 Firebase Manager
 * Handles Firebase operations and real-time updates
 */
class FirebaseManager {
  constructor(config) {
    this.config = config;
    this.unifiedManager = null;
    this.isInitialized = false;
  }

  async initialize() {
    try {
      // Wait for unified Firebase manager to be available
      let retries = 0;
      const maxRetries = 50; // 5 seconds max wait
      
      while (!window.KTUnifiedFirebase && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      
      if (!window.KTUnifiedFirebase) {
        throw new Error('Unified Firebase manager not available after waiting');
      }
      
      // Get unified manager
      this.unifiedManager = window.KTUnifiedFirebase;
      
      // Wait for Firebase to be initialized if not already
      if (!this.unifiedManager.isInitialized()) {
        // Wait for initialization to complete
        await new Promise((resolve, reject) => {
          const checkInitialized = () => {
            if (this.unifiedManager.isInitialized()) {
              resolve();
            } else {
              // Listen for firebaseReady event
              const handler = () => {
                document.removeEventListener('kt-firebase-firebaseReady', handler);
                resolve();
              };
              document.addEventListener('kt-firebase-firebaseReady', handler);
              
              // Also try to initialize if not already initializing
              if (!this.unifiedManager.initialized) {
                this.unifiedManager.initialize().then(resolve).catch(reject);
              }
              
              // Timeout after 10 seconds
              setTimeout(() => {
                document.removeEventListener('kt-firebase-firebaseReady', handler);
                if (!this.unifiedManager.isInitialized()) {
                  reject(new Error('Firebase initialization timeout'));
                } else {
                  resolve();
                }
              }, 10000);
            }
          };
          
          // Check immediately
          checkInitialized();
        });
      }
      
      this.isInitialized = true;
      if (window.logger) {
        window.logger.log('Firebase initialized successfully via unified manager');
      }
      
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      throw error;
    }
  }

  async createSubscription(data) {
    const subscriptionId = this.generateSubscriptionId(data);
    const subscriptionData = {
      ...data,
      id: subscriptionId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'active',
      notification_count: 0,
      last_notification: null
    };

    await this.unifiedManager.setDocument('alert_subscriptions', subscriptionId, subscriptionData);
    return subscriptionId;
  }

  async getSubscription(subscriptionId) {
    return await this.unifiedManager.getDocument('alert_subscriptions', subscriptionId);
  }

  async getSubscriptionsByEmail(email) {
    return await this.unifiedManager.getAlertSubscriptions(email);
  }

  async updateSubscription(subscriptionId, updates) {
    await this.unifiedManager.updateDocument('alert_subscriptions', subscriptionId, {
      ...updates,
      updated_at: new Date().toISOString()
    });
  }

  async deleteSubscription(subscriptionId) {
    await this.unifiedManager.updateDocument('alert_subscriptions', subscriptionId, {
      status: 'deleted',
      updated_at: new Date().toISOString()
    });
  }

  generateSubscriptionId(data) {
    const hash = btoa(`${data.email}-${data.product_id}-${data.variant_id}-${data.alert_type}`);
    return hash.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
  }

  // Real-time subscription to changes
  onSubscriptionChange(subscriptionId, callback) {
    const docRef = doc(this.db, 'alert_subscriptions', subscriptionId);
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }
}

/**
 * 🚀 Main Alert Manager Class
 * Orchestrates all components and provides public API
 */
class KTAlertManager {
  constructor() {
    this.config = new ConfigManager();
    this.firebase = new FirebaseManager(this.config);
    this.notifications = new NotificationManager();
    this.analytics = null;
    this.retry = new RetryManager(this.config);
    
    this.isInitialized = false;
    this.subscriptions = new Map();
  }

  async initialize(options = {}) {
    try {
      // Merge options with config
      if (options.config) {
        Object.assign(this.config.config, options.config);
      }
      
      // Initialize Firebase
      await this.firebase.initialize();
      
      // Initialize notifications
      if (this.config.get('features.pushNotifications')) {
        await this.notifications.initialize();
      }
      
      // Initialize analytics
      if (this.firebase.analytics) {
        this.analytics = new AnalyticsManager(this.firebase.analytics);
      }
      
      this.isInitialized = true;
      
      // Dispatch ready event
      this.dispatchEvent('ready', { manager: this });
      
    } catch (error) {
      console.error('🚀 KT Alert Manager: Initialization failed:', error);
      throw error;
    }
  }

  async subscribeToAlert(data) {
    if (!this.isInitialized) {
      throw new Error('Alert manager not initialized');
    }

    // Validate data
    const validation = ValidationManager.validateSubscriptionData(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    return this.retry.executeWithRetry(async () => {
      try {
        // Check subscription limits
        await this.checkSubscriptionLimits(data);
        
        // Create subscription
        const subscriptionId = await this.firebase.createSubscription(data);
        
        // Set up push notifications if requested
        if (data.notifications?.push && this.config.get('features.pushNotifications')) {
          await this.setupPushNotifications(data);
        }
        
        // Log analytics
        if (this.analytics) {
          this.analytics.logAlertSubscribed(data);
        }
        
        // Track analytics with unified Firebase manager
        if (window.KTUnifiedFirebase) {
          await window.KTUnifiedFirebase.trackAnalytics('back_in_stock_subscription', {
            product_id: data.product_id,
            variant_id: data.variant_id,
            email: data.email,
            customer_id: data.customer_id,
            timestamp: new Date().toISOString()
          });
        }
        
        // Add to live dashboard
        if (window.LiveDataDashboard) {
          await window.LiveDataDashboard.addSubscription(data);
        }
        
        // Set up real-time listener
        this.setupSubscriptionListener(subscriptionId);
        
        return {
          success: true,
          subscription: {
            id: subscriptionId,
            ...data
          }
        };
        
      } catch (error) {
        console.error('🚀 Alert subscription failed:', error);
        if (this.analytics) {
          this.analytics.logError(error, { action: 'subscribe', data });
        }
        throw error;
      }
    });
  }

  async checkSubscriptionLimits(data) {
    const emailSubscriptions = await this.firebase.getSubscriptionsByEmail(data.email);
    
    if (emailSubscriptions.length >= this.config.get('alerts.maxSubscriptionsPerEmail')) {
      throw new Error(`Maximum subscriptions per email (${this.config.get('alerts.maxSubscriptionsPerEmail')}) reached`);
    }
  }

  async setupPushNotifications(data) {
    const hasPermission = await this.notifications.requestPermission();
    if (!hasPermission) {
      console.warn('Push notification permission denied');
      return;
    }

    // Get VAPID public key from unified Firebase manager
    const result = await this.firebase.unifiedManager.getVapidPublicKey();
    const vapidPublicKey = result.publicKey;

    const subscription = await this.notifications.subscribeToPush(vapidPublicKey);
    if (subscription) {
      // Store push subscription in Firebase
      await this.firebase.updateSubscription(data.id, {
        push_subscription: subscription.toJSON()
      });
    }
  }

  setupSubscriptionListener(subscriptionId) {
    const unsubscribe = this.firebase.onSubscriptionChange(subscriptionId, (subscription) => {
      if (subscription) {
        this.subscriptions.set(subscriptionId, subscription);
        this.dispatchEvent('subscriptionUpdated', { subscription });
      } else {
        this.subscriptions.delete(subscriptionId);
        this.dispatchEvent('subscriptionDeleted', { subscriptionId });
      }
    });

    // Store unsubscribe function
    this.subscriptions.set(subscriptionId, { unsubscribe });
  }

  async unsubscribeFromAlert(subscriptionId) {
    if (!this.isInitialized) {
      throw new Error('Alert manager not initialized');
    }

    return this.retry.executeWithRetry(async () => {
      await this.firebase.deleteSubscription(subscriptionId);
      
      // Remove listener
      const subscription = this.subscriptions.get(subscriptionId);
      if (subscription?.unsubscribe) {
        subscription.unsubscribe();
      }
      this.subscriptions.delete(subscriptionId);
      
      return { success: true };
    });
  }

  async getSubscriptions(email) {
    if (!this.isInitialized) {
      throw new Error('Alert manager not initialized');
    }

    return await this.firebase.getSubscriptionsByEmail(email);
  }

  // Event system
  dispatchEvent(eventName, data) {
    const event = new CustomEvent(`kt-alert-${eventName}`, {
      detail: data,
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  // Utility methods
  isInitialized() {
    return this.isInitialized;
  }

  getConfig() {
    return this.config.config;
  }

  getVersion() {
    return '2.0.0';
  }
}

// Create global instance
const ktAlertManager = new KTAlertManager();

// Auto-initialize when DOM is ready, but wait for Firebase to be available
function initializeAlertManager() {
  // Check if Firebase is available
  if (!window.KTUnifiedFirebase) {
    // Wait and retry
    setTimeout(initializeAlertManager, 200);
    return;
  }
  
  // Check if Firebase is initialized
  if (window.KTUnifiedFirebase && !window.KTUnifiedFirebase.isInitialized()) {
    // Wait for Firebase ready event
    const handler = () => {
      document.removeEventListener('kt-firebase-firebaseReady', handler);
      ktAlertManager.initialize().catch(error => {
        console.error('🚀 KT Alert Manager: Auto-initialization failed:', error);
      });
    };
    document.addEventListener('kt-firebase-firebaseReady', handler);
    
    // Also try to initialize Firebase if not already initializing
    if (!window.KTUnifiedFirebase.initialized) {
      window.KTUnifiedFirebase.initialize().then(() => {
        ktAlertManager.initialize().catch(error => {
          console.error('🚀 KT Alert Manager: Auto-initialization failed:', error);
        });
      }).catch(error => {
        console.error('Firebase initialization error:', error);
      });
    }
    
    // Timeout after 10 seconds
    setTimeout(() => {
      document.removeEventListener('kt-firebase-firebaseReady', handler);
      if (window.KTUnifiedFirebase && window.KTUnifiedFirebase.isInitialized()) {
        ktAlertManager.initialize().catch(error => {
          console.error('🚀 KT Alert Manager: Auto-initialization failed:', error);
        });
      }
    }, 10000);
    return;
  }
  
  // Firebase is ready, initialize alert manager
  ktAlertManager.initialize().catch(error => {
    console.error('🚀 KT Alert Manager: Auto-initialization failed:', error);
  });
}

// Start initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAlertManager);
} else {
  // DOM already loaded, start immediately
  initializeAlertManager();
}

// Make available globally
window.KTAlertManager = ktAlertManager;
